import { el } from './dom.js';
import { remember, remembered } from './collapse.js';

/* --------------------------------------------------------------------------
   Sidomenyn: tid och urval till vänster, som i Back office

   Menyn står kvar när man rullar, så att datum, dagar och urval alltid går
   att ändra var man än är på sidan. Varje del — Filen, Tid, Veckodagar och
   klockslag, Sortiment, Urval, Kund — fälls ihop och ut för sig och minns
   det; från början är bara Tid och Veckodagar och klockslag utfällda. En hopfälld del
   visar sitt val i rubriken ("Tid · mars 2026"), så att menyn kan vara kort
   utan att man tappar bort vad som är valt. Delarna finns också i rutan
   bakom Urval på smal skärm. Den fälls ihop till en smal list med
   ikoner, en per del och samma som i delens rubrik, och ut igen. Ett klick på en ikon fäller
   ut menyn och flyttar fokus till den delen. Valet sparas i webbläsaren,
   bara som "hopfälld eller inte".

   Ingen animation: menyn fälls ihop och ut ofta, och innehållet bredvid
   byter bredd — en glidning skulle bara göra det segare.

   På smal skärm finns ingen sidomeny (CSS); urvalet öppnas då med knappen
   Urval i flikraden (ui/quickfilter.js), som flyttar in samma #controls.
   -------------------------------------------------------------------------- */

const KEY = 'sidomeny';

/* Utfällda från början: det man ändrar oftast. Filen, Sortiment, Urval och
   Kund står hopfällda och visar sitt val i rubriken. Den som fäller ut eller
   ihop en del får det sparat, åt båda hållen — därför två nycklar per del:
   "meny-x" för hopfälld och "meny-x-oppen" för utfälld. Utan sparat val
   gäller standarden. */
const OPEN_BY_DEFAULT = new Set(['tid', 'dagar']);
const closedKey = sec => 'meny-' + sec.dataset.sec;
const openKey = sec => 'meny-' + sec.dataset.sec + '-oppen';

function savedOpen(sec){
  if(remembered(closedKey(sec))) return false;
  if(remembered(openKey(sec))) return true;
  return OPEN_BY_DEFAULT.has(sec.dataset.sec);
}

function setOpen(open, save = true){
  el('layout').classList.toggle('side-collapsed', !open);
  el('sideBody').hidden = !open;
  el('btnSidebar').setAttribute('aria-expanded', String(open));
  el('btnSidebarText').textContent = open ? 'Fäll ihop menyn' : 'Fäll ut menyn';
  if(save) remember(KEY, !open);
}

const isOpen = () => !el('layout').classList.contains('side-collapsed');

function setSection(sec, open, save = true){
  const btn = sec.querySelector('.msec-btn');
  btn.setAttribute('aria-expanded', String(open));
  el(btn.getAttribute('aria-controls')).hidden = !open;
  sec.classList.toggle('is-collapsed', !open);
  if(save){ remember(closedKey(sec), !open); remember(openKey(sec), open); }
}

/** Valet i varje del, som står i rubriken när delen är hopfälld. Urval och
 *  kund står ihop i taggarna och kommer därför var för sig från renderTags(). */
export function syncMenuSummaries({urval = '', kund = ''} = {}){
  const text = id => { const e = el(id); return e && !e.hidden ? e.textContent.trim() : ''; };
  el('sumTid').textContent = text('tagTime');
  el('sumDagar').textContent = text('tagDayTime') || 'Alla dagar';
  el('sumUrval').textContent = urval || 'Alla';
  el('sumKund').textContent = kund || 'Alla kunder';
  el('sumSortiment').textContent = text('tagLines') || 'Allt';
  el('sumFil').textContent = text('pRange');
  el('sideCount').textContent = text('tagCount');
}

/** Det första som går att ändra i den del av menyn som ikonen står för. */
function target(id){
  const t = el(id);
  if(t && !t.disabled) return t;
  const part = t && t.closest('.msec');
  return part && [...part.querySelectorAll('input, select, button')].find(e => !e.disabled && e.offsetParent !== null);
}

export function initSidebar(){
  el('btnSidebar').addEventListener('click', () => setOpen(!isOpen()));
  el('sideRail').addEventListener('click', e => {
    const b = e.target.closest('.rail-btn');
    if(!b) return;
    setOpen(true);
    const sec = el(b.dataset.goto)?.closest('.msec');
    if(!sec) return;
    if(sec.classList.contains('is-collapsed')) setSection(sec, true);
    // Delen överst i menyn, så att hela syns. Bara menyn rullar, aldrig sidan
    // (scrollIntoView rullar också fönstret).
    const body = el('sideBody');
    body.scrollTop += sec.getBoundingClientRect().top - body.getBoundingClientRect().top;
    // Fokus på det första som går att ändra; är allt avstängt, på rubriken
    (target(b.dataset.goto) || sec.querySelector('.msec-btn')).focus({preventScroll: true});
  });
  if(remembered(KEY)) setOpen(false, false);
  for(const sec of document.querySelectorAll('.msec')){
    sec.querySelector('.msec-btn').addEventListener('click', () => setSection(sec, sec.classList.contains('is-collapsed')));
    setSection(sec, savedOpen(sec), false);
  }
}
